import React, { useState } from 'react';
import { teacherChangePassword, teacherConfirmNewEmail, teacherRequestChangeEmailOtp, teacherRequestEmailOtp, teacherUpdateEmail, teacherVerifyCurrentEmailOtp, teacherVerifyEmailOtp, updateTeacher } from '../storage';
import { toast, Btn, Card, CardHead, Input, PhotoZone } from '../ui.jsx';

// ── Teacher Profile ───────────────────────────────────────────────
function TeacherProfile({ teacher, db, teacherEmail, setTeacherEmail, activeSessionId }) {
  const photo = (db.tphotos || {})[teacher.tid] || (db.tphotos || {})[teacher.id] ||
    (() => { const t = (db.teachers || []).find(t => (t.puser || t.username) === (teacher.puser || teacher.username)); return t ? (db.tphotos || {})[t.tid || t.id] : null; })() || null;
  const name = (teacher.fn || '') + ' ' + (teacher.ln || '');
  const ini = name.trim().split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const doj = teacher.doj ? new Date(teacher.doj).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';

  const [emailEdit, setEmailEdit] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [currentOtp, setCurrentOtp] = useState('');
  const [newOtp, setNewOtp] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [emailStep, setEmailStep] = useState('idle'); // idle|verify-current|enter-new|verify-new|verify-new-direct|done
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailMsg, setEmailMsg] = useState('');

  const [pwOpen, setPwOpen] = useState(false);
  const [curPw, setCurPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confPw, setConfPw] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState('');

  const resetEmailFlow = () => {
    setEmailEdit(false); setNewEmail(''); setCurrentOtp(''); setNewOtp('');
    setMaskedEmail(''); setEmailStep('idle'); setEmailMsg('');
  };

  // Persist email into localStorage so it survives page refresh
  const persistEmail = (email) => {
    try {
      const raw = localStorage.getItem('lkps_user');
      if (raw) {
        const u = JSON.parse(raw);
        u.em = email;
        localStorage.setItem('lkps_user', JSON.stringify(u));
      }
    } catch (_) {}
  };

  // Click Change/Add button
  const startEmailChange = async () => {
    setEmailMsg('');
    if (!teacherEmail) { setEmailEdit(true); setEmailStep('enter-new'); return; }
    setEmailSaving(true);
    try {
      const r = await teacherRequestChangeEmailOtp();
      setMaskedEmail(r.maskedEmail || '');
      setEmailEdit(true); setEmailStep('verify-current');
    } catch (e) { setEmailMsg(e.message || 'Failed to send OTP'); }
    setEmailSaving(false);
  };

  // Step 1 (change): verify current OTP + enter new email → OTP sent to new email
  const verifyCurrentAndSendNew = async () => {
    if (!currentOtp) { setEmailMsg('Enter the OTP'); return; }
    if (!newEmail || !/\S+@\S+\.\S+/.test(newEmail)) { setEmailMsg('Enter a valid new email'); return; }
    setEmailSaving(true); setEmailMsg('');
    try {
      await teacherVerifyCurrentEmailOtp(currentOtp, newEmail);
      setEmailStep('verify-new');
    } catch (e) { setEmailMsg(e.message || 'Invalid OTP'); setCurrentOtp(''); }
    setEmailSaving(false);
  };

  // Step 2 (change): verify new email OTP → save + sync to admin db
  const confirmNewEmail = async () => {
    if (!newOtp) { setEmailMsg('Enter the OTP'); return; }
    setEmailSaving(true); setEmailMsg('');
    try {
      await teacherConfirmNewEmail(newOtp);
      const tid = teacher.tid || teacher.id;
      if (tid && activeSessionId) { try { await updateTeacher(activeSessionId, tid, { em: newEmail }); } catch (_) { } }
      persistEmail(newEmail);          // ← persist so refresh keeps the email
      setTeacherEmail(newEmail);
      setEmailStep('done');
      setTimeout(() => resetEmailFlow(), 2000);
    } catch (e) {
      setEmailMsg(e.message || 'Invalid OTP');
      setNewOtp(''); setNewEmail(''); setEmailStep('enter-new');
    }
    setEmailSaving(false);
  };

  // First-time add: send OTP to new email
  const sendNewEmailOtp = async () => {
    if (!newEmail || !/\S+@\S+\.\S+/.test(newEmail)) { setEmailMsg('Enter a valid email address'); return; }
    setEmailSaving(true); setEmailMsg('');
    try {
      await teacherRequestEmailOtp(newEmail.trim());
      setEmailStep('verify-new-direct');
    } catch (e) { setEmailMsg(e.message || 'Failed to send OTP'); }
    setEmailSaving(false);
  };

  // First-time add: verify OTP → save + sync to admin db
  const verifyNewEmailDirect = async () => {
    if (!newOtp) { setEmailMsg('Enter the OTP'); return; }
    setEmailSaving(true); setEmailMsg('');
    try {
      await teacherVerifyEmailOtp(newOtp);
      const tid = teacher.tid || teacher.id;
      if (tid && activeSessionId) { try { await updateTeacher(activeSessionId, tid, { em: newEmail }); } catch (_) { } }
      persistEmail(newEmail.trim());   // ← persist so refresh keeps the email
      setTeacherEmail(newEmail.trim());
      setEmailStep('done');
      setTimeout(() => resetEmailFlow(), 2000);
    } catch (e) {
      setEmailMsg(e.message || 'Invalid OTP — please re-enter your email');
      setNewOtp(''); setNewEmail(''); setEmailStep('enter-new');
    }
    setEmailSaving(false);
  };

  const changePw = async () => {
    if (!curPw || !newPw || !confPw) { setPwMsg('Fill all fields'); return; }
    if (newPw !== confPw) { setPwMsg('Passwords do not match'); return; }
    if (newPw.length < 6) { setPwMsg('Min 6 characters'); return; }
    setPwSaving(true); setPwMsg('');
    try {
      await teacherChangePassword(curPw, newPw);
      setPwMsg('Password changed successfully');
      setCurPw(''); setNewPw(''); setConfPw('');
      setPwOpen(false);
    } catch (e) { setPwMsg(e.message || 'Failed'); }
    setPwSaving(false);
  };

  const row = (label, val) => val ? (
    <div style={{ display: 'flex', gap: 16, padding: '12px 0', borderBottom: '1px solid #f1f5f9' }}>
      <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, minWidth: 160 }}>{label}</div>
      <div style={{ fontSize: 13, color: '#1e293b', fontWeight: 600 }}>{val}</div>
    </div>
  ) : null;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>My Profile</div>
        <h1 style={{ fontSize: 28, fontWeight: 900, color: '#1e293b', fontFamily: 'Manrope,sans-serif', margin: 0 }}>{name}</h1>
      </div>

      {/* Email prompt banner if no email */}
      {!teacherEmail && (
        <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 12, padding: '14px 18px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span className="material-symbols-outlined" style={{ fontSize: 20, color: '#d97706', flexShrink: 0 }}>warning</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#92400e' }}>No email registered</div>
            <div style={{ fontSize: 12, color: '#78350f' }}>Add your email to enable password recovery if you forget your credentials.</div>
          </div>
          <button onClick={startEmailChange}
            style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: '#d97706', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer', flexShrink: 0 }}>
            Add Email
          </button>
        </div>
      )}

      {/* Profile card */}
      <div style={{ background: '#fff', borderRadius: 16, padding: '28px', boxShadow: '0 1px 8px rgba(0,31,77,0.06)', marginBottom: 20, display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ width: 100, height: 100, borderRadius: 16, background: 'linear-gradient(135deg,#059669,#10b981)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, fontWeight: 900, color: '#fff', overflow: 'hidden', flexShrink: 0 }}>
          {photo ? <img src={photo} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : ini}
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 22, fontWeight: 900, color: '#1e293b', fontFamily: 'Manrope,sans-serif', marginBottom: 4 }}>{name}</div>
          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>{teacher.desig || 'Teacher'} · {db.settings?.year || ''}</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {teacher.su && <span style={{ background: '#f0fdf4', color: '#059669', fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 20, border: '1px solid #bbf7d0' }}>{teacher.su}</span>}
            {teacher.cls && teacher.cls.split(',').map(x => x.trim()).filter(Boolean).map(c => <span key={c} style={{ background: '#eef4ff', color: '#1960a3', fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 20, border: '1px solid #c7d9f5', marginRight: 4 }}>Class {c}</span>)}
            {teacher.blood && <span style={{ background: '#fef2f2', color: '#dc2626', fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 20, border: '1px solid #fecaca' }}>{teacher.blood}</span>}
          </div>
        </div>
      </div>

      {/* Details */}
      <div style={{ background: '#fff', borderRadius: 16, padding: '20px 24px', boxShadow: '0 1px 8px rgba(0,31,77,0.06)', marginBottom: 20 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: '#1e293b', marginBottom: 4 }}>Professional Information</div>
        {row('Designation', teacher.desig)}
        {row('Subject', teacher.su)}
        {row('Class Teacher', teacher.cls ? teacher.cls : null)}
        {row('Qualification', teacher.qual)}
        {row('Employee ID', teacher.empId)}
        {row('Date of Joining', doj)}
        {row('Status', teacher.status || 'Active')}
        <div style={{ fontSize: 12, fontWeight: 800, color: '#1e293b', margin: '16px 0 4px' }}>Contact</div>
        {row('Phone', teacher.ph)}
        {row('Blood Group', teacher.blood)}
        {/* Email row with OTP verification */}
        <div style={{ display: 'flex', gap: 16, padding: '12px 0', borderBottom: '1px solid #f1f5f9', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, minWidth: 160, paddingTop: 2 }}>Email</div>
          <div style={{ flex: 1, minWidth: 200 }}>

            {/* Idle */}
            {!emailEdit && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, color: '#1e293b', fontWeight: 600 }}>{teacherEmail || <span style={{ color: '#94a3b8' }}>Not set</span>}</span>
                <button onClick={startEmailChange} disabled={emailSaving}
                  style={{ fontSize: 11, fontWeight: 700, color: '#059669', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 20, padding: '3px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 13 }}>edit</span>
                  {emailSaving ? 'Sending OTP…' : teacherEmail ? 'Change' : 'Add'}
                </button>
                {emailMsg && <div style={{ width: '100%', fontSize: 12, color: '#dc2626', fontWeight: 600, marginTop: 4 }}>{emailMsg}</div>}
              </div>
            )}

            {/* Step 1 (change): verify current email OTP + enter new email */}
            {emailEdit && emailStep === 'verify-current' && (
              <div style={{ maxWidth: 360 }}>
                <div style={{ fontSize: 12, color: '#0369a1', fontWeight: 600, marginBottom: 8 }}>OTP sent to <b>{maskedEmail}</b></div>
                {emailMsg && <div style={{ background: '#fef2f2', color: '#dc2626', borderRadius: 8, padding: '7px 10px', fontSize: 12, fontWeight: 600, marginBottom: 8, border: '1px solid #fecaca' }}>{emailMsg}</div>}
                <label style={{ fontSize: 11, fontWeight: 700, color: '#475569', display: 'block', marginBottom: 4 }}>OTP from current email</label>
                <input value={currentOtp} onChange={e => { setCurrentOtp(e.target.value); setEmailMsg(''); }} placeholder="Enter OTP" maxLength={6}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #c7d9f5', fontSize: 13, outline: 'none', boxSizing: 'border-box', marginBottom: 8 }} />
                <label style={{ fontSize: 11, fontWeight: 700, color: '#475569', display: 'block', marginBottom: 4 }}>New email address</label>
                <input value={newEmail} onChange={e => { setNewEmail(e.target.value); setEmailMsg(''); }} type="email" placeholder="new@email.com"
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #c7d9f5', fontSize: 13, outline: 'none', boxSizing: 'border-box', marginBottom: 10 }}
                  onKeyDown={e => e.key === 'Enter' && verifyCurrentAndSendNew()} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={verifyCurrentAndSendNew} disabled={emailSaving}
                    style={{ flex: 1, padding: '9px', borderRadius: 8, border: 'none', background: '#059669', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                    {emailSaving ? 'Verifying…' : 'Verify & Continue'}
                  </button>
                  <button onClick={resetEmailFlow}
                    style={{ padding: '9px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: 'transparent', color: '#64748b', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Enter new email (first-time add, or after wrong OTP) */}
            {emailEdit && emailStep === 'enter-new' && (
              <div style={{ maxWidth: 360 }}>
                {emailMsg && <div style={{ background: '#fef2f2', color: '#dc2626', borderRadius: 8, padding: '7px 10px', fontSize: 12, fontWeight: 600, marginBottom: 8, border: '1px solid #fecaca' }}>{emailMsg}</div>}
                <label style={{ fontSize: 11, fontWeight: 700, color: '#475569', display: 'block', marginBottom: 4 }}>Email address</label>
                <input value={newEmail} onChange={e => { setNewEmail(e.target.value); setEmailMsg(''); }} type="email" placeholder="your@email.com"
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #c7d9f5', fontSize: 13, outline: 'none', boxSizing: 'border-box', marginBottom: 10 }}
                  onKeyDown={e => e.key === 'Enter' && sendNewEmailOtp()} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={sendNewEmailOtp} disabled={emailSaving}
                    style={{ flex: 1, padding: '9px', borderRadius: 8, border: 'none', background: '#059669', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                    {emailSaving ? 'Sending OTP…' : 'Send OTP'}
                  </button>
                  <button onClick={resetEmailFlow}
                    style={{ padding: '9px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: 'transparent', color: '#64748b', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Step 2 (change): verify new email OTP */}
            {emailEdit && emailStep === 'verify-new' && (
              <div style={{ maxWidth: 360 }}>
                <div style={{ fontSize: 12, color: '#0369a1', fontWeight: 600, marginBottom: 8 }}>OTP sent to <b>{newEmail}</b></div>
                {emailMsg && <div style={{ background: '#fef2f2', color: '#dc2626', borderRadius: 8, padding: '7px 10px', fontSize: 12, fontWeight: 600, marginBottom: 8, border: '1px solid #fecaca' }}>{emailMsg}</div>}
                <input value={newOtp} onChange={e => { setNewOtp(e.target.value); setEmailMsg(''); }} placeholder="Enter OTP from new email" maxLength={6}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #c7d9f5', fontSize: 13, outline: 'none', boxSizing: 'border-box', marginBottom: 8 }}
                  onKeyDown={e => e.key === 'Enter' && confirmNewEmail()} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={confirmNewEmail} disabled={emailSaving}
                    style={{ flex: 1, padding: '9px', borderRadius: 8, border: 'none', background: '#059669', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                    {emailSaving ? 'Saving…' : 'Confirm & Save'}
                  </button>
                  <button onClick={() => { setEmailStep('verify-current'); setNewOtp(''); setCurrentOtp(''); setEmailMsg(''); }}
                    style={{ padding: '9px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: 'transparent', color: '#64748b', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
                    Back
                  </button>
                </div>
              </div>
            )}

            {/* Step 2 (first-time add): verify OTP */}
            {emailEdit && emailStep === 'verify-new-direct' && (
              <div style={{ maxWidth: 360 }}>
                <div style={{ fontSize: 12, color: '#0369a1', fontWeight: 600, marginBottom: 8 }}>OTP sent to <b>{newEmail}</b></div>
                {emailMsg && <div style={{ background: '#fef2f2', color: '#dc2626', borderRadius: 8, padding: '7px 10px', fontSize: 12, fontWeight: 600, marginBottom: 8, border: '1px solid #fecaca' }}>{emailMsg}</div>}
                <input value={newOtp} onChange={e => { setNewOtp(e.target.value); setEmailMsg(''); }} placeholder="Enter 6-digit OTP" maxLength={6}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #c7d9f5', fontSize: 13, outline: 'none', boxSizing: 'border-box', marginBottom: 8 }}
                  onKeyDown={e => e.key === 'Enter' && verifyNewEmailDirect()} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={verifyNewEmailDirect} disabled={emailSaving}
                    style={{ flex: 1, padding: '9px', borderRadius: 8, border: 'none', background: '#059669', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                    {emailSaving ? 'Verifying…' : 'Verify & Save'}
                  </button>
                  <button onClick={() => { setEmailStep('enter-new'); setNewOtp(''); setEmailMsg(''); }}
                    style={{ padding: '9px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: 'transparent', color: '#64748b', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
                    Change email
                  </button>
                </div>
              </div>
            )}

            {emailEdit && emailStep === 'done' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#059669', fontSize: 13, fontWeight: 700 }}>
                <span className="material-symbols-outlined" style={{ fontSize: 18, fontVariationSettings: "'FILL' 1" }}>check_circle</span>
                Email updated successfully
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Change Password */}
      <div style={{ background: '#fff', borderRadius: 16, padding: '20px 24px', boxShadow: '0 1px 8px rgba(0,31,77,0.06)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: pwOpen ? 16 : 0 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#1e293b' }}>Change Password</div>
          <button onClick={() => { setPwOpen(v => !v); setPwMsg(''); }}
            style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#1960a3', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
            {pwOpen ? 'Cancel' : 'Change'}
          </button>
        </div>
        {pwOpen && <>
          {pwMsg && <div style={{ fontSize: 12, color: pwMsg.includes('success') ? '#059669' : '#dc2626', marginBottom: 10, fontWeight: 600 }}>{pwMsg}</div>}
          {[['Current Password', curPw, setCurPw], ['New Password', newPw, setNewPw], ['Confirm New Password', confPw, setConfPw]].map(([lbl, val, set]) => (
            <div key={lbl} style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 4 }}>{lbl}</label>
              <input type="password" value={val} onChange={e => set(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
            </div>
          ))}
          <button onClick={changePw} disabled={pwSaving}
            style={{ width: '100%', padding: '11px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#1e293b,#334155)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
            {pwSaving ? 'Saving…' : 'Update Password'}
          </button>
        </>}
      </div>
    </div>
  );
}

export default TeacherProfile;
